/**
 * Provider Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { InlineFilterBar, InlineMultiSelect, InlineToggles } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { ProviderStatus, ProviderType } from '../../../types/provider';

interface ProviderFiltersData {
  searchTerm: string;
  status: string[];
  providerTypes: string[];
}

interface ProviderFiltersProps {
  onFilter: (filters: ProviderFiltersData) => void;
  loading?: boolean;
  total?: number;
  /** Só é verdadeiro quando existe alguma clínica prospectada — sem uma, o recorte nem é desenhado. */
  mostrarRecorteDeProspectadas?: boolean;
  apenasProspectadas?: boolean;
  onRecorteChange?: (apenasProspectadas: boolean) => void;
}

const STATUS_LABELS: Record<ProviderStatus, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  SUSPENSO: 'Suspenso',
  BLOQUEADO: 'Bloqueado',
  PENDENTE: 'Pendente',
};

const TYPE_LABELS: Record<ProviderType, string> = {
  CLINICA: 'Clínica',
  LABORATORIO: 'Laboratório',
  HOSPITAL: 'Hospital',
  CONSULTORIO: 'Consultório',
  CENTRO_MEDICO: 'Centro Médico',
  AMBIENTAL: 'Ambiental',
  SEGURANCA: 'Segurança',
  MULTIPLO: 'Múltiplo',
};

const statusOptions = (Object.values(ProviderStatus).filter(
  (value) => typeof value === 'string',
) as ProviderStatus[]).map((s) => ({ value: s, label: STATUS_LABELS[s] }));

const typeOptions = (Object.values(ProviderType).filter(
  (value) => typeof value === 'string',
) as ProviderType[]).map((t) => ({ value: t, label: TYPE_LABELS[t] }));

const defaultValues: ProviderFiltersData = {
  searchTerm: '',
  status: [],
  providerTypes: [],
};

const ProviderFilters: React.FC<ProviderFiltersProps> = ({
  onFilter,
  loading = false,
  total,
  mostrarRecorteDeProspectadas = false,
  apenasProspectadas = false,
  onRecorteChange,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      status: 'Status',
      providerTypes: 'Tipo',
    },
    formatValue: {
      status: (v: string[]) => v.map((s) => STATUS_LABELS[s as ProviderStatus] ?? s).join(', '),
      providerTypes: (v: string[]) =>
        v.map((t) => TYPE_LABELS[t as ProviderType] ?? t).join(', '),
    },
  });

  // A clínica prospectada nasce pendente: mantido o filtro de situação, o recorte abriria vazio e
  // pareceria integração quebrada.
  const alternarRecorte = (apenas: boolean) => {
    if (apenas) {
      setFilter('status', []);
    }
    onRecorteChange?.(apenas);
  };

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome ou CNPJ..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="prestadores"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      {mostrarRecorteDeProspectadas && (
        <InlineToggles
          items={[
            {
              label: 'Prestadores cadastrados',
              checked: !apenasProspectadas,
              onChange: () => alternarRecorte(false),
            },
            {
              label: 'Clínicas prospectadas',
              checked: apenasProspectadas,
              onChange: () => alternarRecorte(true),
            },
          ]}
        />
      )}
      {!apenasProspectadas && (
        <InlineMultiSelect
          label="Status"
          value={filters.status}
          options={statusOptions}
          onChange={(v) => setFilter('status', v)}
          minWidth={140}
        />
      )}
      <InlineMultiSelect
        label="Tipo"
        value={filters.providerTypes}
        options={typeOptions}
        onChange={(v) => setFilter('providerTypes', v)}
        minWidth={150}
      />
    </InlineFilterBar>
  );
};

export default ProviderFilters;
